import type { PendingEdit } from '../../hooks/usePendingCellEdits';
import type { ForecastVolumeEdit } from '../../services/budgetBillingConfig';
import type { OnTopCostEdit } from '../../services/budgetOnTopCost';

/** The Ist-Budget row of the summary grid. One row, so its pending edits need a constant id. */
export const BASELINE_EDIT_KIND = 'baselineEdits';
export const BASELINE_ROW_ID = 0;

// Turning the Overview's one pending set into requests (CALL-779). Three resources, three shapes — the
// page's Save fires one request per resource that has edits, so these are separate builders rather than
// one payload.

/** Handlingdaten: month deltas per billing config. */
export const forecastPayload = (edits: PendingEdit[], kind: string): ForecastVolumeEdit[] => {
    const byConfig = new Map<number, ForecastVolumeEdit>();

    for (const edit of edits) {
        if (edit.kind !== kind) continue;
        const entry = byConfig.get(edit.rowId) ?? { billingConfigId: edit.rowId, forecastVolumeEdits: [] };
        entry.forecastVolumeEdits.push({ month: edit.key, value: edit.value as number | null });
        byConfig.set(edit.rowId, entry);
    }

    return [...byConfig.values()];
};

/** On Top Costs: changed columns and month deltas per cost. */
export const onTopPayload = (edits: PendingEdit[], valueKind: string): OnTopCostEdit[] => {
    const byCost = new Map<number, OnTopCostEdit>();

    for (const edit of edits) {
        if (edit.kind !== 'field' && edit.kind !== valueKind) continue;
        const entry = byCost.get(edit.rowId) ?? { id: edit.rowId };

        if (edit.kind === 'field') {
            entry.fields = { ...entry.fields, [edit.key]: edit.value };
        } else {
            entry.valueEdits = [...(entry.valueEdits ?? []), { month: edit.key, value: edit.value as number | null }];
        }

        byCost.set(edit.rowId, entry);
    }

    return [...byCost.values()];
};

/**
 * Ist Budget: month deltas, like the other three series.
 *
 * It used to send the WHOLE series, because its endpoint replaced it — which made every save a chance to
 * delete the version's budget, since the client assembled that series from its summary query and a query
 * that is loading, errored or garbage-collected looks exactly like "there are no baselines". The endpoint
 * now applies deltas (CALL-779), so an untouched month is untouchable and `value: null` clears one.
 */
export const baselinePayload = (edits: PendingEdit[]): Array<{ month: string; value: number | null }> =>
    edits
        .filter((edit) => edit.kind === BASELINE_EDIT_KIND)
        .map((edit) => ({ month: edit.key, value: edit.value as number | null }));
